import {
  forEach,
  intersection,
  isArray,
  isEmpty,
  uniq,
  cloneDeep,
  omit,
  pick,
} from 'lodash';
import { useMemo } from 'react';
import useUrlFilters from './useUrlFilters';
import { getVocabulary } from 'components/Vocabulary';
import { useGlobalFilterContext } from 'contexts/GlobalFilterContext';
import { convertDateFilterValueToDate } from 'utils/dateUtils';
import moment from 'moment';
import useDetectFilterChange from './useDetectFilterChange';
import { useSkipGlobalFiltersContext } from 'contexts/SkipGlobalFiltersContext';

export default function useFindingFiltersByGlobalFilters(
  filters,
  filterKeyMap,
  { supportedFilterKeys } = {}
) {
  const { skipGlobalFilters } = useSkipGlobalFiltersContext();
  const {
    foundOn: foundOnKey = 'foundOn',
    environmentName: environmentNameKey = 'environmentName',
  } = filterKeyMap || {};
  const { globalFilterProducts, globalFilterSubProducts, globalFilterGroups } =
    useGlobalFilterContext();
  const productFromUrl = useUrlFilters('product');
  const subProductFromUrl = useUrlFilters('subProduct');
  const envFromUrl = useUrlFilters('environmentName');
  const sourceFromUrl = useUrlFilters('source');
  const scanTypeFromUrl = useUrlFilters('scanType');
  const categoryFromUrl = useUrlFilters('armorcodeCategory');
  const groupsFromUrl = useUrlFilters('groups');
  const selectedDurationFromUrl = useUrlFilters('selectedDuration');
  const foundOnFromUrl = useUrlFilters('foundOn');
  const allFiltersFromUrl = useUrlFilters();

  const actualAppliedFilters = useDetectFilterChange(allFiltersFromUrl);

  const finalFiltersFromUrl = useMemo(() => {
    return omit(supportedFilterKeys ? pick(actualAppliedFilters, supportedFilterKeys) : actualAppliedFilters, [
      'product',
      'subProduct',
      'environmentName',
      'scanType',
      'armorcodeCategory',
      'groups',
      'foundOn',
    ]);
  }, [actualAppliedFilters, supportedFilterKeys]);

  // Prune unsupported filter keys once — shared by both skip (Anya) and non-skip (Dashboard) paths.
  // This must be in a useMemo (not a plain variable) to maintain a stable object reference,
  // otherwise widgets would get a new filters object every render, causing infinite useEffect → API loops.
  const prunedFilters = useMemo(() => {
    if (isEmpty(supportedFilterKeys)) return filters;
    return isArray(filters)
      ? filters.map((f) => pick(f, supportedFilterKeys))
      : pick(filters, supportedFilterKeys);
  }, [filters, supportedFilterKeys]);

  const mergedFilters = useMemo(() => {
    // Skip all merge work in Anya context — avoids reacting to URL/global filter changes
    if (skipGlobalFilters) return null;

    let allFilters = prunedFilters;
    if (!isArray(allFilters)) {
      allFilters = [allFilters];
    }

    const allModifiedFilters = [];
    const errors = [];
    forEach(allFilters, (_filters) => {
      try {
        const finalFilters = cloneDeep(_filters);
        applyFilterFromUrl(
          finalFilters,
          'product',
          productFromUrl,
          globalFilterProducts,
          `${getVocabulary(
            'product'
          )} filter selected at dashboard and widget are completely different`
        );
        applyFilterFromUrl(
          finalFilters,
          'subProduct',
          subProductFromUrl,
          globalFilterSubProducts,
          `${getVocabulary(
            'subProduct'
          )} filter selected at dashboard and widget are completely different`
        );
        applyFilterFromUrl(
          finalFilters,
          environmentNameKey,
          envFromUrl,
          [],
          'Environment filter selected at dashboard and widget are completely different'
        );
        applyFilterFromUrl(
          finalFilters,
          'source',
          sourceFromUrl,
          [],
          'Source filter selected at dashboard and widget are completely different'
        );
        applyFilterFromUrl(
          finalFilters,
          'scanType',
          scanTypeFromUrl,
          [],
          'Scan Type filter selected at dashboard and widget are completely different'
        );
        applyFilterFromUrl(
          finalFilters,
          'armorcodeCategory',
          categoryFromUrl,
          [],
          'Finding Category filter selected at dashboard and widget are completely different'
        );

        applyFilterFromUrlForHierarchy(
          finalFilters,
          'groups',
          groupsFromUrl,
          globalFilterGroups,
          `${getVocabulary(
            'group'
          )} filter selected at dashboard and widget are completely different`
        );

        applyDateFilter(
          finalFilters,
          selectedDurationFromUrl,
          foundOnFromUrl,
          foundOnKey
        );

        forEach(finalFiltersFromUrl, (values, key) => {
          applyFilterFromUrl(
            finalFilters,
            key,
            values,
            [],
            `${key} filter selected at dashboard and widget are completely different`
          );
        });

        allModifiedFilters.push(finalFilters);
      } catch (e) {
        // ENG-56371 - skip saved searches that don't match the dashboard/global filter and send the rest that matches
        // previously we used to skip all if any saved search didn't match
          errors.push(e);
      }
    });
    if (allModifiedFilters.length === 0 && errors.length > 0) {
      throw errors[0];
    }
    if (!isArray(filters)) {
      return allModifiedFilters[0];
    }
    return allModifiedFilters;
  }, [prunedFilters, productFromUrl, subProductFromUrl, envFromUrl, sourceFromUrl, scanTypeFromUrl, categoryFromUrl, groupsFromUrl, globalFilterGroups, globalFilterProducts, globalFilterSubProducts, selectedDurationFromUrl, foundOnFromUrl, foundOnKey, environmentNameKey, finalFiltersFromUrl, skipGlobalFilters]);

  return skipGlobalFilters ? prunedFilters : mergedFilters;
}

export function applyFilterFromUrl(
  filters,
  filterKey,
  filterFromUrl,
  filterFromGlobalFilter,
  errorMessage
) {
  const allFilters = [
    filters[filterKey],
    filterFromUrl,
    filterFromGlobalFilter,
  ].filter((f) => !isEmpty(f));
  if(filterKey === 'keyValue'){
    allFilters.forEach(filterValues => {
      forEach(filterValues, (keyValue, i) => {
        if(keyValue.key || keyValue.value){
          filterValues[i] = JSON.stringify(keyValue);
        }
      })
    })
  }
  if (allFilters.length === 0) {
    return;
  }
  const commonValues = intersection(...allFilters);
  if (commonValues.length) {
    if(filterKey === 'keyValue'){
      filters[filterKey] = commonValues.map(keyValueString => JSON.parse(keyValueString));
    } else {
      filters[filterKey] = commonValues;
    }
  } else {
    const error = new Error(errorMessage, { cause: 'Filter' });
    throw error;
  }
}

function applyFilterFromUrlForHierarchy(
  filters,
  filterKey,
  filterFromUrl,
  filterFromGlobalFilter,
  errorMessage
) {
  const allFilters = [
    filters[filterKey],
    filterFromUrl,
    filterFromGlobalFilter,
  ].filter((f) => !isEmpty(f));
  if (allFilters.length === 0) {
    return;
  }
  if (allFilters.length === 1) {
    filters[filterKey] = allFilters[0];
    return;
  }
  let commonValues = allFilters[0];
  for (let index = 0; index < allFilters.length - index; index++) {
    const currentArry = [...commonValues];
    const nextArray = allFilters[index + 1];
    // We are checking of a hierarchy is a child of another hierarchy then pick the child hierarchy,
    // as we are using regex for parent selection, we cannot use normal intersection/difference
    // ENG-37987
    commonValues = uniq([
      ...customIntersection(currentArry, nextArray),
      ...customIntersection(nextArray, currentArry),
    ]);
  }

  if (commonValues.length) {
    filters[filterKey] = commonValues;
  } else {
    const error = new Error(errorMessage, { cause: 'Filter' });
    throw error;
  }
}

function customIntersection(first, second) {
  const common = [];
  forEach(first, (value) => {
    if (!value.endsWith('/.*')) {
      forEach(second, (value2) => {
        if (value === value2) {
          common.push(value2);
        }
      });
      return;
    }
    forEach(second, (value2) => {
      if (value2.startsWith(value.replace('/.*', ''))) {
        common.push(value2);
      }
    });
  });
  return common;
}

export function applyDateFilter(
  finalFilters,
  selectedDurationFromUrl,
  foundOnFromUrl,
  filterKey = 'foundOn'
) {
  // This method computes overlaping dates if selected
  // Eg: (1st Jan - 20th Jan) AND (10th Jan - 15th Jan) query will return data for (10th Jan - 15th Jan) as it is the overlapping date range
  // Eg: (1st Jan - 20th Jan) AND (10th Jan - 25th Jan) query will return data for (10th Jan - 20th Jan) as it is the overlapping date range
  if (!isEmpty(selectedDurationFromUrl) || !isEmpty(foundOnFromUrl)) {
    if (isEmpty(finalFilters[filterKey])) {
      finalFilters[filterKey] = convertDateFilterValueToDate(
        undefined,
        isEmpty(selectedDurationFromUrl)
          ? foundOnFromUrl
          : [selectedDurationFromUrl]
      );
    } else {
      const [startDateUrl, endDateUrl] = convertDateFilterValueToDate(
        undefined,
        isEmpty(selectedDurationFromUrl)
          ? foundOnFromUrl
          : [selectedDurationFromUrl]
      ).map((t) => moment(t));

      const [startDateFilter, endDateFilter] = convertDateFilterValueToDate(
        undefined,
        finalFilters[filterKey]
      ).map((t) => moment(t));

      let startDate, endDate;
      if (startDateFilter.isAfter(startDateUrl)) {
        startDate = startDateFilter;
      } else {
        startDate = startDateUrl;
      }

      if (endDateFilter.isAfter(endDateUrl)) {
        endDate = endDateUrl;
      } else {
        endDate = endDateFilter;
      }

      if (startDate && endDate && startDate.isBefore(endDate)) {
        finalFilters[filterKey] = [
          startDate.utc().format(),
          endDate.utc().format(),
        ];
      } else {
        const error = new Error(
          'Date/Found On range selected at dashboard and widget are completely different',
          { cause: 'Filter' }
        );
        throw error;
      }

      console.log(`finalFilters.${filterKey}`, finalFilters[filterKey]);
    }
  }
}
